import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SvgIcon, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import MuiDialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { styled, Theme, useTheme } from '@mui/material/styles';

import { FunctionComponent } from '@sbv-clients/util-misc';

import { useWindowedDialog } from '../../hooks';
import { Icon } from '../icon';
import IconButton from '../icon-button/icon-button';
import { useDialogClose } from './dialog-close-context';
import { DialogCloseGuard } from './dialog-close-guard';
import {
  DialogWindowedControlsContext,
  useDialogWindowedControls,
} from './dialog-windowed-controls-context';
import { DragHandleClassContext, DraggablePaper } from './draggable';
import { WindowedTooltipZIndexContext } from './windowed-tooltip-zindex-context';

const DialogAmbientZIndexContext = createContext<number | undefined>(undefined);

const getWindowedDialogSx = (theme: Theme) => ({
  pointerEvents: 'none',
  '& .MuiPaper-root': {
    pointerEvents: 'auto',
    position: 'fixed',
    top: 'auto',
    left: 'auto',
    right: 16,
    bottom: 16,
    margin: 0,
    width: '420px !important',
    maxWidth: 'calc(100vw - 32px) !important',
    height: 'clamp(420px, 55vh, 640px) !important',
    maxHeight: 'calc(100vh - 32px) !important',
    minWidth: 'unset !important',
    minHeight: 'unset !important',
    borderRadius: '14px',
    overflow: 'hidden',
    border: 'none',
    transform: 'none !important',
    boxShadow: theme.custom?.windowedDialogShadow,
  },
});

const getDefaultDialogSx = (theme: Theme) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: theme.custom?.dialogShadow,
  },
});

const SMALL_SCREEN_DIALOG_SX = {
  '& .MuiDialog-paper': {
    maxWidth: 'calc(100% - 32px) !important',
  },
} as const;

const WINDOWED_MIN_HEIGHT = 300;
const WINDOWED_MAX_HEIGHT_MARGIN = 32;

const WindowedResizeHandle = ({
  onMouseDown,
}: {
  onMouseDown: (event: React.MouseEvent) => void;
}) => {
  const theme = useTheme();
  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '6px',
        zIndex: 1,
        cursor: 'ns-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        '&:hover': { backgroundColor: theme.custom?.iconButtonHover },
        '&::after': {
          content: '""',
          display: 'block',
          width: '40px',
          height: '3px',
          borderRadius: '2px',
          backgroundColor: theme.custom?.controlBorder,
        },
      }}
    />
  );
};

const Dialog = ({
  windowed: windowedProp,
  isMinimizeable,
  sx,
  children,
  open,
  ...props
}: DialogProps & { windowed?: boolean; isMinimizeable?: boolean }) => {
  const internalWindowedDialog = useWindowedDialog();
  const windowed = isMinimizeable
    ? internalWindowedDialog.windowed
    : windowedProp;

  const [customHeight, setCustomHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (isMinimizeable && open) {
      internalWindowedDialog.reset();
      setCustomHeight(undefined);
    }
  }, [open, isMinimizeable]);

  const sxArray = Array.isArray(sx) ? sx : sx ? [sx] : [];
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [paperElement, setPaperElement] = useState<HTMLDivElement | null>(null);
  const paperRefCallback = useCallback((node: HTMLDivElement | null) => {
    setPaperElement(node);
  }, []);

  const resizeDragState = useRef({ startY: 0, startHeight: 0 });
  const onResizeMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const startHeight =
        paperElement?.getBoundingClientRect().height ?? WINDOWED_MIN_HEIGHT;
      resizeDragState.current = { startY: event.clientY, startHeight };

      const onMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientY - resizeDragState.current.startY;
        const maxHeight = window.innerHeight - WINDOWED_MAX_HEIGHT_MARGIN;
        const nextHeight = Math.min(
          maxHeight,
          Math.max(
            WINDOWED_MIN_HEIGHT,
            resizeDragState.current.startHeight - delta
          )
        );
        setCustomHeight(nextHeight);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [paperElement]
  );

  const ambientZIndex = useContext(DialogAmbientZIndexContext);
  const dialogZIndex =
    windowed || ambientZIndex !== undefined
      ? Math.max(
          windowed ? theme.zIndex.tooltip + 1 : theme.zIndex.modal,
          (ambientZIndex ?? 0) + 1
        )
      : undefined;

  return (
    <MuiDialog
      PaperComponent={DraggablePaper}
      open={open}
      {...props}
      hideBackdrop={windowed || props.hideBackdrop}
      disableScrollLock={windowed || props.disableScrollLock}
      disableEnforceFocus={windowed || props.disableEnforceFocus}
      sx={[
        ...(windowed
          ? [getWindowedDialogSx(theme)]
          : [getDefaultDialogSx(theme)]),
        ...(isSmallScreen && !windowed && !props.fullScreen
          ? [SMALL_SCREEN_DIALOG_SX]
          : []),
        ...(windowed && customHeight !== undefined
          ? [{ '& .MuiPaper-root': { height: `${customHeight}px !important` } }]
          : []),
        ...(dialogZIndex !== undefined ? [{ zIndex: dialogZIndex }] : []),
        ...sxArray,
      ]}
      PaperProps={{ ...props.PaperProps, ref: paperRefCallback }}
    >
      {windowed && <WindowedResizeHandle onMouseDown={onResizeMouseDown} />}
      <WindowedTooltipZIndexContext.Provider
        value={
          dialogZIndex !== undefined
            ? {
                zIndex: dialogZIndex + 1,
                boundaryElement: windowed ? paperElement : null,
              }
            : undefined
        }
      >
        <DialogAmbientZIndexContext.Provider
          value={dialogZIndex ?? ambientZIndex}
        >
          <DialogWindowedControlsContext.Provider
            value={
              isMinimizeable
                ? {
                    windowed: internalWindowedDialog.windowed,
                    toggleWindowed: internalWindowedDialog.toggleWindowed,
                  }
                : undefined
            }
          >
            {children}
          </DialogWindowedControlsContext.Provider>
        </DialogAmbientZIndexContext.Provider>
      </WindowedTooltipZIndexContext.Provider>
    </MuiDialog>
  );
};

const DialogFormFlexWrapper: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      minHeight: 0,
      '& > form': {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
      },
    }}
  >
    {children}
  </Box>
);

const StyledDialogContent = styled(Box)`
  overflow-y: auto;
  max-height: 60vh;
  padding: ${(props) => `${props.theme.spacing(4)} ${props.theme.spacing(6)}`};
`;

const headerButtonSx = (theme: Theme) => ({
  width: 30,
  height: 30,
  borderRadius: '8px',
  border: 'none',
  background: 'none',
  color: theme.custom?.iconButton,
  '&:hover': {
    background: theme.custom?.iconButtonHover,
    color: theme.custom?.brandNavy,
  },
});

// The dialog header's minimize / restore and close buttons. Also used on
// their own when a dialog's title row is something else (e.g. a table card's
// header, as in the Audits dialog).
const DialogHeaderControls = ({
  onClose,
  windowed: windowedProp,
  onToggleWindowed: onToggleWindowedProp,
}: {
  onClose?: () => void;
  windowed?: boolean;
  onToggleWindowed?: () => void;
}) => {
  const dialogClose = useDialogClose();
  const dialogControls = useDialogWindowedControls();
  const windowed = windowedProp ?? dialogControls?.windowed ?? false;
  const onToggleWindowed =
    onToggleWindowedProp ?? dialogControls?.toggleWindowed;
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      {onToggleWindowed && (
        <IconButton
          size="small"
          aria-label={windowed ? 'Expand' : 'Minimize'}
          title={windowed ? 'Expand' : 'Minimize'}
          onClick={(event) => {
            event.stopPropagation();
            onToggleWindowed();
          }}
          sx={headerButtonSx(theme)}
        >
          <SvgIcon
            viewBox="0 0 24 24"
            sx={{
              width: 17,
              height: 17,
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          >
            <path
              d={
                windowed
                  ? 'M14 4h6v6M20 4l-6 6M10 20H4v-6M4 20l6-6'
                  : 'M14 10l6-6M14 10V5.5M14 10h4.5M10 14l-6 6M10 14v4.5M10 14H5.5'
              }
            />
          </SvgIcon>
        </IconButton>
      )}
      {onClose && (
        <IconButton
          size="small"
          aria-label="Close"
          onClick={(event) => {
            event.stopPropagation();
            (dialogClose ?? onClose)();
          }}
          sx={headerButtonSx(theme)}
        >
          <Icon
            name={'Close'}
            iconProps={{
              fontSize: 'small',
              sx: { fontSize: 18, color: 'inherit' },
            }}
          />
        </IconButton>
      )}
    </Box>
  );
};

// Class that makes an element the dialog's drag handle (none while windowed,
// as the windowed panel is pinned).
const useDialogDragHandleClass = () => {
  const dragHandleClass = useContext(DragHandleClassContext);
  const windowed = useDialogWindowedControls()?.windowed ?? false;
  return windowed ? undefined : dragHandleClass;
};

const DialogHeader = ({
  title,
  subTitle,
  onClose,
  icon,
  windowed: windowedProp,
  onToggleWindowed: onToggleWindowedProp,
}: {
  title: string;
  subTitle?: string;
  onClose?: () => void;
  icon?: any;
  windowed?: boolean;
  onToggleWindowed?: () => void;
}) => {
  const dragHandleClass = useContext(DragHandleClassContext);
  const dialogControls = useDialogWindowedControls();
  const windowed = windowedProp ?? dialogControls?.windowed ?? false;
  const theme = useTheme();

  return (
    <DialogTitle
      id="draggable-dialog-title"
      className={windowed ? undefined : dragHandleClass}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px',
        cursor: windowed ? 'default' : 'move',
        borderBottom: `1px solid ${
          windowed
            ? theme.custom?.windowedHeaderLine
            : theme.custom?.pinnedColumnLine
        }`,
        ...(windowed && {
          padding: '16px 12px 10px 18px',
          fontSize: '15px',
          fontWeight: 600,
          lineHeight: '21px',
        }),
      }}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {icon}
          {title}
        </Box>
        {subTitle && (
          <Box
            style={{
              color: theme.text.secondary,
              fontWeight: 400,
              marginTop: 6,
            }}
          >
            {' ' + subTitle}
          </Box>
        )}
      </Box>
      <DialogHeaderControls
        onClose={onClose}
        windowed={windowedProp}
        onToggleWindowed={onToggleWindowedProp}
      />
    </DialogTitle>
  );
};

const DialogActionsContainer: FunctionComponent = ({ children }) => {
  const theme = useTheme();
  return (
    <>
      <Box sx={{ marginTop: theme.spacing(3) }} />
      <DialogActions
        sx={{
          padding: '14px 22px',
          gap: '8px',
          borderTop: `1px solid ${theme.custom?.dialogFooterLine}`,
          flexWrap: 'wrap',
          '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
          containerType: 'inline-size',
          '@container (max-width: 460px)': {
            '& .MuiButton-root': {
              paddingLeft: '12px',
              paddingRight: '12px',
              minWidth: 0,
            },
          },
        }}
      >
        {children}
      </DialogActions>
    </>
  );
};

export {
  Dialog,
  DialogFormFlexWrapper,
  DialogContentText,
  DialogActions,
  DialogContent,
  StyledDialogContent,
  DialogTitle,
  DialogHeader,
  DialogHeaderControls,
  useDialogDragHandleClass,
  DialogActionsContainer,
  DialogCloseGuard,
  useDialogClose,
};
